import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import Flyout from '../../../client/components/Overlays/GenericFlyout';
import Menu from '../../../client/components/Overlays/Menu';
import CustomFlyoutHeader from '../../../client/components/Overlays/CustomFlyoutHeader';
import useMenuItemChecks, { isMenuItemEnabled } from '../../hooks/useMenuItemCheck';
import BellaGaTracking from 'Common/ga';
import { MenuItemType, MenuItemInterface } from 'Client/components/Overlays/Menu/types';
import { OverlayId } from 'Client/components/Overlays/types';
import { hideOverlay } from 'Client/redux/header/overlays/actions';
import useTranslator from 'Client/hooks/useTranslator';
import useConfig from 'AppShell/hooks/useConfig';
import { VictorQaClasses } from 'Common/qaIds';
import { BellaTranslator, Config, FeatureToggles, SegmentTracking } from 'AppShell/appshell_types';
import paths from 'Common/routes/paths';
import useGa from 'Client/hooks/useGa';
import { ensureEndingSlash } from 'Client/urlService';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { useSegmentTracking } from 'Common/segment';

export const createMenuItems = ({
    t,
    config,
    gaTracking,
    segmentTracker,
    featureToggles,
}: {
    t: BellaTranslator;
    config: Config;
    gaTracking: BellaGaTracking;
    segmentTracker: SegmentTracking;
    featureToggles: FeatureToggles;
}): MenuItemInterface[] =>
    [
        {
            title: t('Account overview'),
            url: config.links.myAccount,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuOverview,
        },
        {
            title: t('My Wishlist'),
            url: config.links.wishList,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuWishlist,
        },
        {
            title: t('My Looks Wishlist'),
            url: config.links.myLooks,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuMyLooks,
        },
        featureToggles.interiorServiceInNav && {
            title: t('Myaccount_Studio'),
            url: ensureEndingSlash(paths.INTERIOR_DESIGN_SERVICE),
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            isNew: true,
            trackItemClick: () =>
                gaTracking.trackGenericEvent('Interior Service Tracking', {
                    action: 'Burger menu click',
                    label: 'My account click',
                }),
        },
        {
            title: t('My orders'),
            url: config.links.myOrders,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuOrders,
        },
        {
            title: t('reviews_Product review'),
            url: config.links.myReviews,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuReviews,
        },
        {
            title: t('My Vouchers'),
            url: config.links.myCoupons,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuVoucher,
        },
        {
            title: t('My credentials'),
            url: config.links.myEditAccount,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
        },
        {
            title: t('My Addresses'),
            url: config.links.myAddress,
            itemType: MenuItemType.ExternalLinkArrowed,
            fullLoginRequired: true,
            className: VictorQaClasses.accountMenuAddress,
        },
        {
            title: t('My newsletter'),
            url: config.links.myNewsletter,
            itemType: MenuItemType.ExternalLinkArrowed,
            className: VictorQaClasses.accountMenuNewletter,
        },
        {
            title: t('My notifications'),
            url: config.links.myNotifications,
            itemType: MenuItemType.ExternalLinkArrowed,
        },
        {
            title: t('Refer_a_friend_menu_item'),
            url: `${config.links.clubBaseUrl}${config.links.clubReferAFriendPath}`,
            itemType: MenuItemType.ExternalLinkArrowed,
        },
        {
            title: t('Logout'),
            url: config.links.logout,
            itemType: MenuItemType.ExternalLink,
            navDownCallback: () => {
                segmentTracker.reset();
            },
        },
    ].filter(Boolean) as MenuItemInterface[];

export const AccountMenus: FC<{ closeAction?: () => void }> = ({ closeAction = () => {} }) => {
    const t = useTranslator();
    const config = useConfig();
    const gaTracking = useGa();
    const itemChecks = useMenuItemChecks(config);
    const featureToggles = useFeatureToggle();
    const segmentTracker = useSegmentTracking();
    const menuItems = createMenuItems({
        t,
        config,
        gaTracking,
        segmentTracker,
        featureToggles,
    }).filter(({ checkKey }) => isMenuItemEnabled(itemChecks, checkKey));
    const menuSections = [
        {
            key: 'accountMenu',
            title: '',
            items: menuItems,
        },
    ];

    return <Menu menuSections={menuSections} closeMenuFn={closeAction} />;
};

export const AccountFlyout: React.FunctionComponent = () => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const closeFunction = () => dispatch(hideOverlay(OverlayId.ACCOUNT_MENU));

    return (
        <Flyout identifier={OverlayId.ACCOUNT_MENU} hasCustomHeader>
            <CustomFlyoutHeader shouldHaveBackBtn={false} title={t('My account')} handleCloseBtnClick={closeFunction} />
            <AccountMenus closeAction={closeFunction} />
        </Flyout>
    );
};

export default AccountFlyout;
